/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import { ARViewerWeb } from '.'

/** Responsible for displaying camera output in the AR view */
export default class CameraPlugin {
  ar: ARViewerWeb
  player: HTMLVideoElement | undefined
  stream: MediaStream | undefined
  canvas: HTMLCanvasElement
  canvasCtx: any
  previousData: any

  constructor(ar: ARViewerWeb) {
    // Store AR
    this.ar = ar

    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText =
      'position: absolute; z-index: 1; top: 0px; left: 0px; width: 100%; height: 100%; '
  }

  get name() {
    return 'CameraPlugin'
  }

  async load(div: HTMLElement) {
    // Show status
    this.ar.setState({ loadingText: 'Accessing camera...' })

    // Create player element
    this.player = document.createElement('video')
    this.player.setAttribute('autoplay', 'true')
    this.player.setAttribute('loop', 'true')
    this.player.setAttribute('muted', 'true')
    this.player.setAttribute('playsinline', 'true')
    this.player.style.cssText =
      'position: absolute; z-index: 1; top: 0px; left: 0px; ' +
      (this.ar.getSetting('ar.camera-sync') ? '' : 'width: 100%; height: 100%; object-fit: cover; ')
    div.appendChild(this.player)

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment'
      }
    })

    this.player.srcObject = this.stream!
    this.player.play()

    // Create canvas
    div.appendChild(this.canvas)

    // Get canvas context
    this.canvasCtx = this.canvas.getContext('2d')

    // Attach resize handler
    window.addEventListener('resize', this.onResize)
    this.onResize()

    // Done
    this.ar.clearStatus('camera')
  }

  unload() {
    // Remove listeners
    window.removeEventListener('resize', this.onResize)

    // Stop all tracks in the stream
    for (const track of this.stream?.getTracks() || []) track.stop()

    // Remove player element
    if (this.player?.parentNode) this.player.parentNode.removeChild(this.player)
  }

  /** @private Called when the view is resized */
  onResize = () => {
    if (this.canvas) {
      // Get size of canvas
      const rect = this.canvas.getBoundingClientRect()

      // Update renderer size
      this.canvas.width = rect.width
      this.canvas.height = rect.height
    }
  }

  /** Called by the 3D plugin before it renders a frame */
  onRender3D(delta: number, scene: any, camera: any) {
    // Stop if disabled
    if (!this.ar.getSetting('ar.camera-sync') || !this.player || !this.canvas) return

    // Get size of the video player element
    // TODO: Cache this, is getBoundingClientRect() a heavy function to call every frame?
    const videoSize = this.player!.getBoundingClientRect()

    // Work out correct size so the video is scaled/centered correctly.
    // From https://riptutorial.com/html5-canvas/example/19169/scaling-image-to-fit-or-fill-
    const scale = Math.max(
      this.canvas.width / videoSize.width,
      this.canvas.height / videoSize.height
    )
    const width = videoSize.width * scale
    const height = videoSize.height * scale

    // Render video frame to the canvas
    const x = this.canvas.width / 2 - width / 2
    const y = this.canvas.height / 2 - height / 2
    this.canvasCtx.drawImage(this.player, x, y, width, height)

    // HACK: Some devices (iPhone) seem to have camera lag when rendering a camera and 3D content at the same time. So here
    // we check each frame, and skip rendering the 3D content if the frame is exactly the same.
    // Get new buffer
    const newImageData = this.canvasCtx.getImageData(0, 0, this.canvas.width, this.canvas.height)

    // If no previous buffer, just save this one
    if (!this.previousData) {
      this.previousData = newImageData
      return
    }

    // Compare all pixels
    const dataLength = Math.min(newImageData.data.length, this.previousData.data.length)
    let isExactMatch = true
    for (let i = 0; i < dataLength; i++) {
      // Check if pixel data matches
      if (newImageData.data[i] === this.previousData.data[i]) continue

      // No match
      isExactMatch = false
      break
    }

    // Store buffer
    this.previousData = newImageData

    // Stop rendering if it's an exact match
    if (isExactMatch) return false

    return
  }
}
